<template>
  <!-- 水印不展示时（外部 show 覆盖为 false，或非试用版）不渲染水印包装，直接透出插槽内容 -->
  <slot v-if="!watermarkVisible"></slot>
  <!-- 全屏安全水印：覆盖默认插槽内容；水印层 pointer-events 为 none，不拦截下层地图与控件交互。
       外部 class/style（如 layout-watermark 的撑满样式）经 $attrs 落到 ElWatermark 根节点 -->
  <ElWatermark v-else v-bind="$attrs" :content="finalContent" :font="finalFont" :gap="gap" :z-index="zIndex">
    <slot></slot>
  </ElWatermark>
</template>

<script setup name="TmWatermark">
/**
 * 全屏安全水印公共组件（基于 ElWatermark 封装）。
 * 默认两行展示“租户名称 / 公司品牌”（租户名称取登录时写入的本地存储，品牌文案取国际化 watermark.company），
 * 默认样式适配暗色主题：低透明度白色、加粗、稀疏间距；全部可通过 props 覆盖。
 * 组件内置防篡改（ElWatermark 的 MutationObserver）与内容变化自动重绘。
 *
 * 主要 props：
 * - content {string | string[]} 自定义水印内容；为数组时按多行渲染，为空时回退为“租户名称 / 品牌”两行
 * - show {boolean} 可选，外部直接控制水印显隐；不传时按本地存储的套餐版本判断
 * - color {string} 水印文字颜色，默认 rgba(255, 255, 255, 0.18)（暗色界面适用）
 * - fontSize {number} 水印字号（px），默认 26
 * - gap {number[]} 水印水平/垂直间距（px），默认 [280, 200]
 * - zIndex {number} 水印层级，默认 130：高于常规页面层级（≤120），低于传送至 body 的弹窗（2000+）
 *
 * 插槽：default —— 需要加水印的页面内容；组件会作为其包装容器。
 *
 * 开关：由本地存储的 packageEdition 驱动（登录页登录成功时写入），
 * 仅 TRIAL（试用版）渲染水印；正式版或无记录时不渲染，各调用页面无需任何改动。
 * 登录页等需要在登录前就响应式控制显隐的场景，可通过 show prop 覆盖。
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// 双根模板（显/隐两个分支）无法自动继承外部属性：关闭自动继承并改为在展示分支手动绑定 $attrs，
// 使外部 class（如 layout-watermark 的 height:100% 撑满样式）真正作用到水印包装层，同时消除运行时警告
defineOptions({ inheritAttrs: false });

const props = defineProps({
  // 自定义水印内容；为数组时按多行渲染，为空时回退为“租户名称 / 品牌”两行展示
  content: {
    type: [String, Array],
    default: ''
  },
  // 外部显隐覆盖：登录页选择租户时即时联动；不传（undefined）时回退为本地存储的套餐版本判断
  show: {
    type: Boolean,
    default: undefined
  },
  // 水印文字颜色；默认低透明度白色，避免干扰深色地图与蓝色业务图形
  color: {
    type: String,
    default: 'rgba(255, 255, 255, 0.1)'
  },
  // 水印字号（px）；默认 26 保证在卫星地图与暗色页面上可辨认
  fontSize: {
    type: Number,
    default: 60
  },
  // 水印水平/垂直间距（px）；默认稀疏布局，避免密集水印影响读图
  gap: {
    type: Array,
    default: () => [280, 200]
  },
  // 水印层级；默认 130，覆盖页内内容且不遮挡弹窗、下拉等传送层
  zIndex: {
    type: Number,
    default: 130
  }
});

// 当前租户是否为试用版：登录时由 login 页把所选租户的套餐版本写入本地存储，此处仅在组件创建时读取一次。
const isTrialEdition = localStorage.getItem('packageEdition') === 'TRIAL';

/**
 * 水印最终是否展示。
 * 外部传入 show 时以外部为准（如登录页随租户下拉实时联动）；未传时按本地存储的套餐版本判断。
 */
const watermarkVisible = computed(() => (props.show !== undefined ? props.show : isTrialEdition));

const { t } = useI18n();

/**
 * 实际渲染的水印内容。
 * 未传 content（或传空）时按“租户名称 / 公司品牌”两行展示；租户名称由登录页登录成功时写入本地存储，
 * 为空（如未登录或未配置）时仅展示品牌一行。语言为响应式来源，变化后 ElWatermark 会自动重绘。
 */
const finalContent = computed(() => {
  if (Array.isArray(props.content) ? props.content.length > 0 : Boolean(props.content)) {
    return props.content;
  }
  // ElWatermark 的 content 为数组时按多行渲染：第一行租户名称，第二行品牌。
  return [localStorage.getItem('tenantName'), t('watermark.company')].filter(Boolean);
});

/** 水印文字样式：统一加粗，颜色与字号由 props 决定。 */
const finalFont = computed(() => ({ color: props.color, fontSize: props.fontSize, fontWeight: 'bold' }));
</script>
